import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Calendar, FileText, Users, BookOpen, GraduationCap, Award, 
  MessageSquare, MapPin, Clock, Download, Phone, Info, UserCircle,
  ClipboardList, Beaker, ListChecks, Gamepad2, Globe, PenLine, Radio, Tv,
  Monitor, Bot, Cog, Star, Trophy, Timer, Medal, Megaphone, CalendarCheck, BookOpen as YearbookIcon,
  Map, Layers, HelpCircle, CreditCard, Send
} from "lucide-react";

const STORAGE_KEY = "ycps_quick_access_favorites";

const getFavorites = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch { return []; }
};

export const QuickLinks = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState(getFavorites);

  const links = [
    { icon: Calendar, label: "Academic Calendar", color: "bg-blue-100 text-blue-600", path: "/academic-calendar" },
    { icon: FileText, label: "Student Handbook", color: "bg-green-100 text-green-600", path: "/resources/student-handbook" },
    { icon: Users, label: "Parent Resources", color: "bg-purple-100 text-purple-600", path: "/resources" },
    { icon: BookOpen, label: "Library Portal", color: "bg-orange-100 text-orange-600", path: "https://www.library.gov.gh/signin", external: true },
    { icon: GraduationCap, label: "JHS Exams Prep", color: "bg-pink-100 text-pink-600", path: "/jhs-exams-prep" },
    { icon: Award, label: "Athletics", color: "bg-yellow-100 text-yellow-600", path: "/athletics" },
    { icon: MessageSquare, label: "News & Updates", color: "bg-teal-100 text-teal-600", path: "/announcements" },
    { icon: MapPin, label: "Campus Map", color: "bg-red-100 text-red-600", path: "/campus-map" },
    { icon: Clock, label: "Bell Schedule", color: "bg-indigo-100 text-indigo-600", path: "/bell-schedule" },
    { icon: Download, label: "Forms & Documents", color: "bg-cyan-100 text-cyan-600", path: "/forms-documents" },
    { icon: Phone, label: "Contact Directory", color: "bg-amber-100 text-amber-600", path: "/contact" },
    { icon: UserCircle, label: "Staff Directory", color: "bg-slate-100 text-slate-600", path: "/staff" },
    { icon: Info, label: "FAQ", color: "bg-lime-100 text-lime-600", path: "/faq" },
    { icon: ClipboardList, label: "Assignments", color: "bg-orange-100 text-orange-600", path: "/course-catalog" },
    { icon: Beaker, label: "STEM Activities", color: "bg-violet-100 text-violet-600", path: "/stem-activities" },
    { icon: ListChecks, label: "School Agenda", color: "bg-emerald-100 text-emerald-600", path: "/school-agenda" },
    { icon: Gamepad2, label: "Games", color: "bg-rose-100 text-rose-600", path: "/games" },
    { icon: Globe, label: "School Clubs", color: "bg-sky-100 text-sky-600", path: "/school-clubs" },
    { icon: PenLine, label: "Blog", color: "bg-fuchsia-100 text-fuchsia-600", path: "/blog" },
    { icon: Radio, label: "School Podcast", color: "bg-amber-100 text-amber-600", path: "/school-podcast" },
    { icon: Tv, label: "School TV", color: "bg-blue-100 text-blue-600", path: "/school-tv" },
    { icon: Monitor, label: "Remote Education", color: "bg-cyan-100 text-cyan-600", path: "/remote-education" },
    { icon: Bot, label: "Robo-Teach", color: "bg-teal-100 text-teal-600", path: "/robot-teacher" },
    { icon: Cog, label: "Robotics", color: "bg-violet-100 text-violet-600", path: "/robotics" },
    { icon: Trophy, label: "Achievements", color: "bg-amber-100 text-amber-600", path: "/achievements" },
    { icon: Timer, label: "Study Timer", color: "bg-red-100 text-red-600", path: "/study-timer" },
    { icon: Medal, label: "Leaderboard", color: "bg-yellow-100 text-yellow-600", path: "/leaderboard" },
    { icon: Megaphone, label: "Shoutout Board", color: "bg-pink-100 text-pink-600", path: "/shoutout-board" },
    { icon: CalendarCheck, label: "Appointments", color: "bg-indigo-100 text-indigo-600", path: "/appointments" },
    { icon: YearbookIcon, label: "Yearbook", color: "bg-emerald-100 text-emerald-600", path: "/yearbook" },
    { icon: Map, label: "Virtual Tour", color: "bg-orange-100 text-orange-600", path: "/virtual-tour" },
    { icon: Layers, label: "My Portfolio", color: "bg-cyan-100 text-cyan-600", path: "/portfolio" },
    { icon: HelpCircle, label: "Quizzes", color: "bg-violet-100 text-violet-600", path: "/quizzes" },
  ];

  const toggleFavorite = useCallback((label, e) => {
    e.stopPropagation();
    setFavorites(prev => {
      const next = prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const handleClick = (link) => {
    if (link.external) {
      window.open(link.path, '_blank');
    } else {
      navigate(link.path);
    }
  };

  const isFav = (label) => favorites.includes(label);

  const sorted = [...links].sort((a, b) => {
    const aFav = isFav(a.label) ? 0 : 1;
    const bFav = isFav(b.label) ? 0 : 1;
    return aFav - bFav;
  });

  const favCount = favorites.length;

  return (
    <section className="py-10 bg-gradient-to-b from-yellow-400 to-orange-400">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-2xl font-bold text-[#002147] mb-1 text-center">Quick Access</h2>
        <p className="text-center text-[#002147]/60 text-xs mb-5">Click the star to pin your favorites to the top</p>

        {favCount > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <Star size={12} className="text-[#FF6B00] fill-[#FF6B00]" />
            <span className="text-[10px] font-semibold text-[#002147]/70 uppercase tracking-wide">My Favorites ({favCount})</span>
            <div className="flex-1 h-px bg-[#002147]/10" />
          </div>
        )}

        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-11 gap-2.5">
          {sorted.map((link, index) => {
            const fav = isFav(link.label);
            const isFirstNonFav = favCount > 0 && index === favCount;
            return (
              <React.Fragment key={link.label}>
                {isFirstNonFav && (
                  <div className="col-span-4 sm:col-span-5 md:col-span-7 lg:col-span-11 flex items-center gap-2 -mb-1">
                    <span className="text-[10px] font-semibold text-[#002147]/50 uppercase tracking-wide">All Shortcuts</span>
                    <div className="flex-1 h-px bg-[#002147]/10" />
                  </div>
                )}
                <button
                  onClick={() => handleClick(link)}
                  className={`relative flex flex-col items-center p-2.5 rounded-xl border backdrop-blur-sm shadow-[0_4px_0_rgba(0,0,0,0.1),0_6px_12px_rgba(0,0,0,0.06)] hover:shadow-[0_2px_0_rgba(0,0,0,0.1),0_4px_8px_rgba(0,0,0,0.04)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all duration-150 ease-out group cursor-pointer select-none ${
                    fav
                      ? "border-[#FF6B00]/40 bg-gradient-to-b from-white/60 to-white/30 ring-1 ring-[#FF6B00]/20"
                      : "border-white/40 bg-gradient-to-b from-white/40 to-white/15"
                  }`}
                  data-testid={`quick-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div
                    onClick={(e) => toggleFavorite(link.label, e)}
                    className={`absolute top-1 right-1 w-4 h-4 rounded-full flex items-center justify-center transition-all duration-200 ${
                      fav
                        ? "text-[#FF6B00] scale-100"
                        : "text-[#002147]/20 opacity-0 group-hover:opacity-100 hover:text-[#FF6B00]/60"
                    }`}
                    data-testid={`fav-toggle-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Star size={10} className={fav ? "fill-[#FF6B00]" : ""} />
                  </div>
                  <div className={`w-11 h-11 rounded-xl ${link.color} flex items-center justify-center mb-2 shadow-sm group-hover:scale-105 group-active:scale-95 transition-transform duration-150`}>
                    <link.icon size={20} />
                  </div>
                  <span className="text-xs font-bold text-[#002147] text-center leading-tight">{link.label}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default QuickLinks;
